import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Save, 
  Loader2, 
  Plus, 
  Trash2, 
  GripVertical,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Mail,
  Send,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSurveyPage = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Test email state
  const [testEmail, setTestEmail] = useState('');
  const [testCustomerName, setTestCustomerName] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/admin/survey-questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data.questions || []);
    } catch (err) {
      setError('Kunne ikke hente spørgsmål');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API}/admin/survey-questions`, 
        { questions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Spørgeskema gemt');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke gemme');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Vil du nulstille til standard-spørgsmål? Dette kan ikke fortrydes.')) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API}/admin/survey-questions/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data.questions || []);
      setSuccess('Spørgeskema nulstillet');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Kunne ikke nulstille');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) {
      setError('Indtast en email-adresse');
      return;
    }
    
    setSendingTest(true);
    setTestResult(null);
    setError('');
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API}/admin/survey/send-test-email`,
        { 
          email: testEmail,
          customer_name: testCustomerName.trim() || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTestResult({
        success: true,
        message: response.data.message,
        surveyUrl: response.data.survey_url,
        customerName: response.data.customer_name
      });
    } catch (err) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || 'Kunne ikke sende test-email'
      });
    } finally {
      setSendingTest(false);
    }
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const updateOption = (qIndex, oIndex, field, value) => {
    const updated = [...questions];
    const options = [...(updated[qIndex].options || [])];
    options[oIndex] = { ...options[oIndex], [field]: value };
    updated[qIndex] = { ...updated[qIndex], options };
    setQuestions(updated);
  };

  const addOption = (qIndex) => {
    const updated = [...questions];
    const options = [...(updated[qIndex].options || [])];
    options.push({ value: `option_${Date.now()}`, label: '' });
    updated[qIndex] = { ...updated[qIndex], options };
    setQuestions(updated);
  };

  const removeOption = (qIndex, oIndex) => {
    const updated = [...questions];
    const options = [...(updated[qIndex].options || [])];
    options.splice(oIndex, 1);
    updated[qIndex] = { ...updated[qIndex], options };
    setQuestions(updated);
  };

  const addQuestion = () => {
    const newQ = {
      id: `q_${Date.now()}`,
      question: '',
      type: 'radio',
      options: [
        { value: 'option_1', label: '' },
        { value: 'option_2', label: '' }
      ]
    };
    setQuestions([...questions, newQ]);
  };

  const removeQuestion = (index) => {
    if (!window.confirm('Vil du slette dette spørgsmål?')) return;
    const updated = [...questions];
    updated.splice(index, 1);
    setQuestions(updated);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-outfit">Spørgeskema</h1>
          <p className="text-slate-500 text-sm mt-1">
            Rediger de spørgsmål kunderne får før besøget
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset} disabled={saving}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Nulstil
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-slate-900">
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Gem ændringer
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-100 rounded-lg text-red-600">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-4 bg-emerald-50 border border-emerald-100 rounded-lg text-emerald-600">
          <CheckCircle2 className="h-4 w-4" />
          {success}
        </div>
      )}

      {/* Test Email Section */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg text-blue-900">Test survey-email</CardTitle>
          </div>
          <CardDescription className="text-blue-700">
            Send en test-email for at se hvordan kunden modtager survey-mailen
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex gap-3">
              <Input
                type="text"
                placeholder="Kundens navn (valgfrit)..."
                value={testCustomerName}
                onChange={(e) => setTestCustomerName(e.target.value)}
                className="flex-1 bg-white"
                data-testid="test-customer-name-input"
              />
            </div>
            <div className="flex gap-3">
              <Input
                type="email"
                placeholder="Din email-adresse..."
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="flex-1 bg-white"
                data-testid="test-email-input"
              />
              <Button 
                onClick={handleSendTestEmail}
                disabled={sendingTest || !testEmail.trim()}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="send-test-email-btn"
              >
                {sendingTest ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send test
                  </>
                )}
              </Button>
            </div>
          </div>
          
          {testResult && (
            <div className={`mt-3 p-3 rounded-lg flex items-start gap-2 ${
              testResult.success 
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {testResult.success ? (
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className="text-sm">{testResult.message}</p>
                {testResult.surveyUrl && (
                  <a 
                    href={testResult.surveyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm underline flex items-center gap-1 mt-1 hover:opacity-80"
                    data-testid="test-survey-link"
                  >
                    Åbn survey-side direkte
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="space-y-4">
        <QuestionsList 
          questions={questions}
          updateQuestion={updateQuestion}
          updateOption={updateOption}
          addOption={addOption}
          removeOption={removeOption}
          removeQuestion={removeQuestion}
        />

        <Button variant="outline" onClick={addQuestion} className="w-full h-12 border-dashed">
          <Plus className="h-4 w-4 mr-2" />
          Tilføj spørgsmål
        </Button>
      </div>
    </div>
  );
};

// Separate component to avoid inline mapping
function QuestionsList({ questions, updateQuestion, updateOption, addOption, removeOption, removeQuestion }) {
  const items = [];
  
  for (let qIndex = 0; qIndex < questions.length; qIndex++) {
    const q = questions[qIndex];
    
    items.push(
      <Card key={q.id || qIndex} className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <GripVertical className="h-5 w-5 text-slate-400" />
              <Badge variant="outline" className="text-xs">
                Spørgsmål {qIndex + 1}
              </Badge>
              <Badge 
                className={q.type === 'textarea' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}
              >
                {q.type === 'textarea' ? 'Fritekst' : 'Valgmuligheder'}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeQuestion(qIndex)}
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-700">Spørgsmål</Label>
            <Input
              value={q.question || ''}
              onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
              placeholder="Skriv dit spørgsmål..."
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-slate-700">Type</Label>
            <select
              value={q.type || 'radio'}
              onChange={(e) => updateQuestion(qIndex, 'type', e.target.value)}
              className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white"
            >
              <option value="radio">Valgmuligheder (radio)</option>
              <option value="textarea">Fritekst</option>
            </select>
          </div>

          {q.type === 'textarea' ? (
            <div>
              <Label className="text-slate-700">Placeholder tekst</Label>
              <Input
                value={q.placeholder || ''}
                onChange={(e) => updateQuestion(qIndex, 'placeholder', e.target.value)}
                placeholder="Hjælpetekst der vises i feltet..."
                className="mt-1"
              />
            </div>
          ) : (
            <div>
              <Label className="text-slate-700 mb-2 block">Svarmuligheder</Label>
              <OptionsList
                options={q.options || []}
                qIndex={qIndex}
                updateOption={updateOption}
                removeOption={removeOption}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => addOption(qIndex)}
                className="mt-2"
              >
                <Plus className="h-3 w-3 mr-1" />
                Tilføj svarmulighed
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }
  
  return <div className="space-y-4">{items}</div>;
}

// Separate component for options
function OptionsList({ options, qIndex, updateOption, removeOption }) {
  const items = [];
  
  for (let oIndex = 0; oIndex < options.length; oIndex++) {
    const opt = options[oIndex];
    
    items.push(
      <div key={oIndex} className="flex items-center gap-2">
        <Input
          value={opt.label || ''}
          onChange={(e) => updateOption(qIndex, oIndex, 'label', e.target.value)}
          placeholder="Svarmulighed..."
          className="flex-1"
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => removeOption(qIndex, oIndex)}
          className="text-slate-400 hover:text-red-500"
          disabled={options.length <= 2}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    );
  }
  
  return <div className="space-y-2">{items}</div>;
}

export default AdminSurveyPage;
